import { useEffect, useRef, useState } from "react";
import {
  TAPE_HZ, TAPE_MAX_N, TAPE_MAX_SEC,
  decodeTape, encodeTape, linkToTape, tapeToLink,
  type FlightTape, type TapeMeta,
} from "../flightTape";

export type TapeEntry = { id: string; name: string; tape: FlightTape };

let tapeSeq = 0;
const newId = () => "t" + Date.now().toString(36) + "-" + tapeSeq++;

/**
 * Owns flight tapes: the rolling recorder buffers (written per-frame by
 * FpvRig at a fixed 30 Hz cadence), the SESSION LIST of finished tapes
 * (RAM-only — they vanish on refresh, the panel says so), replay state, and
 * the share transports (#flight= links + .flight files). Recording state is
 * mirrored into a ref so the 60 fps flight loop reads it without re-renders.
 */
export function useFlightTape() {
  const [tapes, setTapes] = useState<TapeEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [loop, setLoop] = useState(true);
  const [status, setStatus] = useState("");

  const active = tapes.find((t) => t.id === activeId) ?? tapes[tapes.length - 1] ?? null;
  const tape = active?.tape ?? null; // the selected tape — what replay/share act on

  const rec = useRef({
    on: false,
    acc: 0,
    n: 0,
    meta: { fisheye: 0, ground: true, groundAlt: 12, groundDark: 0.35 } as TapeMeta,
    pos: new Float32Array(TAPE_MAX_N * 3),
    quat: new Float32Array(TAPE_MAX_N * 4),
    sticks: new Float32Array(TAPE_MAX_N * 4),
    audio: new Float32Array(TAPE_MAX_N * 5),
  });
  const replaySticks = useRef<[number, number, number, number]>([0, 0, 0, 0]);

  const fmt = (sec: number) => Math.floor(sec / 60) + ":" + String(Math.floor(sec % 60)).padStart(2, "0");
  const clock = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  function addTape(t: FlightTape, label: string) {
    const entry: TapeEntry = { id: newId(), name: label, tape: t };
    setTapes((list) => [...list, entry]);
    setActiveId(entry.id);
    return entry;
  }

  function finalize() {
    const r = rec.current;
    r.on = false;
    setRecording(false);
    if (r.n < TAPE_HZ) { setStatus("tape too short — hold Record for at least a second"); return; }
    const t: FlightTape = {
      v: 1, hz: TAPE_HZ, n: r.n,
      pos: r.pos.slice(0, r.n * 3),
      quat: r.quat.slice(0, r.n * 4),
      sticks: r.sticks.slice(0, r.n * 4),
      audio: r.audio.slice(0, r.n * 5),
      meta: { ...r.meta },
    };
    addTape(t, fmt(r.n / TAPE_HZ) + " · " + clock());
    setStatus(fmt(r.n / TAPE_HZ) + " tape ready — replay it or share it");
  }

  function startRecord(meta: TapeMeta) {
    const r = rec.current;
    r.on = true; r.acc = 0; r.n = 0; r.meta = { ...meta };
    setRecording(true);
    setStatus("");
  }

  function stopRecord() {
    if (rec.current.on) finalize();
  }

  function toggleRecord(meta: TapeMeta) {
    if (rec.current.on) finalize();
    else startRecord(meta);
  }

  /**
   * Called from the FPV frame loop. Samples at a fixed 30 Hz regardless of
   * frame rate; auto-stops at the 60 s cap.
   */
  function push(
    px: number, py: number, pz: number,
    qx: number, qy: number, qz: number, qw: number,
    thr: number, yaw: number, pitch: number, roll: number,
    level: number, bass: number, mid: number, treble: number, beat: number,
    dt: number
  ) {
    const r = rec.current;
    if (!r.on) return;
    r.acc += dt;
    const step = 1 / TAPE_HZ;
    while (r.acc >= step && r.n < TAPE_MAX_N) {
      r.acc -= step;
      const n = r.n;
      r.pos[n * 3] = px; r.pos[n * 3 + 1] = py; r.pos[n * 3 + 2] = pz;
      r.quat[n * 4] = qx; r.quat[n * 4 + 1] = qy; r.quat[n * 4 + 2] = qz; r.quat[n * 4 + 3] = qw;
      r.sticks[n * 4] = thr; r.sticks[n * 4 + 1] = yaw; r.sticks[n * 4 + 2] = pitch; r.sticks[n * 4 + 3] = roll;
      r.audio[n * 5] = level; r.audio[n * 5 + 1] = bass; r.audio[n * 5 + 2] = mid; r.audio[n * 5 + 3] = treble; r.audio[n * 5 + 4] = beat;
      r.n++;
    }
    if (r.n >= TAPE_MAX_N) finalize(); // hit the cap — the tape is full
  }

  const recSeconds = () => rec.current.n / TAPE_HZ;

  function startReplay(id?: string) {
    stopRecord();
    if (id) setActiveId(id);
    const target = id ? tapes.find((t) => t.id === id) : active;
    if (target) { setReplaying(true); setStatus(""); }
  }
  function stopReplay() { setReplaying(false); }

  function deleteTape(id: string) {
    setTapes((list) => list.filter((t) => t.id !== id));
    if (activeId === id) {
      setActiveId(null); // active falls back to the newest remaining
      if (replaying && active?.id === id) setReplaying(false);
    }
  }

  function clearTapes() {
    setTapes([]);
    setActiveId(null);
    setReplaying(false);
    setStatus("");
  }

  function copyLink() {
    if (!tape) return;
    try {
      const link = location.origin + location.pathname + "#flight=" + tapeToLink(tape);
      const kb = Math.round(link.length / 1024);
      navigator.clipboard.writeText(link).then(
        () => setStatus("link copied · " + kb + " KB" + (kb > 60 ? " — long URL; the .flight file is sturdier" : "")),
        () => setStatus("clipboard blocked — save the .flight file instead")
      );
    } catch {
      setStatus("couldn't build the link — save the .flight file instead");
    }
  }

  function saveFile() {
    if (!tape) return;
    const bytes = encodeTape(tape);
    const ab = new ArrayBuffer(bytes.length);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab], { type: "application/octet-stream" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "swarm-flight-" + fmt(tape.n / tape.hz).replace(":", "m") + "s.flight";
    a.click();
    URL.revokeObjectURL(a.href);
    setStatus("saved · " + Math.round(bytes.length / 1024) + " KB");
  }

  async function loadFile(f: File) {
    try {
      const buf = new Uint8Array(await f.arrayBuffer());
      const t = decodeTape(buf);
      addTape(t, fmt(t.n / t.hz) + " · " + (f.name.replace(/\.flight$/i, "") || "loaded"));
      setStatus(fmt(t.n / t.hz) + " tape loaded — press Replay");
    } catch {
      setStatus("that file isn't a readable flight tape");
    }
  }

  // a shared #flight= link boots straight into replay
  useEffect(() => {
    const m = location.hash.match(/#flight=([A-Za-z0-9_-]+)/);
    if (!m) return;
    try {
      const t = linkToTape(m[1]);
      addTape(t, fmt(t.n / t.hz) + " · shared");
      setReplaying(true);
      setStatus("shared flight loaded · " + fmt(t.n / t.hz));
    } catch {
      setStatus("couldn't read that flight link");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tape, tapes, activeId: active?.id ?? null, setActiveId,
    recording, replaying, loop, setLoop, status,
    rec, replaySticks, recSeconds,
    toggleRecord, stopRecord, push, startReplay, stopReplay,
    deleteTape, clearTapes,
    copyLink, saveFile, loadFile,
    maxSec: TAPE_MAX_SEC,
  };
}
